import React, {useCallback} from 'react';

import {Analytics} from '../../../../services/analytics';
import * as CustomRunColorsActions from '../../../../state/views/customRunColors/actions';
import {usePart, useViewAction} from '../../../../state/views/hooks';
import {usePanelsSharedRefsContext} from '../../../Views/contexts/PanelsSharedRefsContext';
import {ColorPalette} from '../colorPalette/ColorPalette';
import {
  RunColorOptions,
  SemanticLegendSettingsWithCategoricalConfig,
  YAxisValues,
} from '../types';
import {SemanticLegendBucketsWithCategoricalConfig} from './SemanticLegendBucketsWithCategoricalConfig';
import {SemanticLegendCategories} from './SemanticLegendCategories';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from './SemanticLegendDefaults';
import {SemanticLegendExcludedRunColor} from './SemanticLegendExcludedRunColor';
import {SemanticLegendKeyWithCategoricalConfig} from './SemanticLegendKeyWithCategoricalConfig';
import {SemanticLegendNumBucketsWithCategoricalConfig} from './SemanticLegendNumBucketsWithCategoricalConfig';
import {SemanticLegendRunColorOptions} from './SemanticLegendRunColorOptions';
import {useSemanticLegendSettingsContext} from './SemanticLegendSettingsWithCategoricalConfigContextProvider';
import {SemanticLegendYValueWithCategoricalConfig} from './SemanticLegendYValueWithCategoricalConfig';
import {useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig} from './utils/useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig';

export const SemanticLegendColorTabWithCategoricalConfig = () => {
  const {
    hasActiveGrouping,
    configKeys,
    semanticLegendSettings,
    semanticLegendRunColorOptions,
    isMetricBasedColors,
    updateSemanticLegendRunColorOptions,
    updateSemanticLegendSettings,
    isLoadingBuckets,
  } = useSemanticLegendSettingsContext();

  const {customRunColorsRef} = usePanelsSharedRefsContext();
  const customRunColors = usePart(customRunColorsRef);
  const numRunsWithCustomColors = Object.keys(customRunColors).length;

  const getSemanticLegendPalette =
    useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig();

  const showMetricBasedColors = !hasActiveGrouping && isMetricBasedColors;
  const updateSetting = useCallback(
    (value: Partial<SemanticLegendSettingsWithCategoricalConfig>) => {
      updateSemanticLegendSettings?.({
        ...semanticLegendSettings,
        ...value,
      });
    },
    [semanticLegendSettings, updateSemanticLegendSettings]
  );
  const numRunsWithCustomColorsText =
    numRunsWithCustomColors === 1
      ? `${numRunsWithCustomColors} run has a custom color. `
      : `${numRunsWithCustomColors} runs have custom colors. `;

  const setCustomRunColor = useViewAction(
    customRunColorsRef,
    CustomRunColorsActions.setCustomRunColor
  );

  const clearCustomColors = useCallback(() => {
    Object.keys(customRunColors).forEach(runId => {
      setCustomRunColor(runId, null);
    });
  }, [customRunColors, setCustomRunColor]);

  return (
    <div
      data-test="workspace-semantic-legend-color-settings"
      className={'flex h-full w-full flex-col gap-20 py-16'}>
      <SemanticLegendRunColorOptions
        hasActiveGrouping={hasActiveGrouping}
        runColorOptions={
          semanticLegendRunColorOptions ?? RunColorOptions.OriginalProjectColors
        }
        onRunColorOptionsChange={value => {
          Analytics.track('Run color options changed', {newValue: value});
          updateSemanticLegendRunColorOptions?.(value);
        }}
      />

      {(showMetricBasedColors || numRunsWithCustomColors > 0) && (
        <div className="ml-10 flex h-full w-full flex-col gap-10 border-l-2 border-moon-350">
          {showMetricBasedColors && (
            <>
              <SemanticLegendKeyWithCategoricalConfig
                currentKey={semanticLegendSettings?.key ?? ''}
                type={
                  semanticLegendSettings?.yAxisValue ??
                  DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue
                }
                configKeys={configKeys}
                updateSetting={updateSetting}
              />
              {semanticLegendSettings.key !==
                DEFAULT_SEMANTIC_LEGEND_SETTINGS.key && (
                <SemanticLegendYValueWithCategoricalConfig
                  yValue={semanticLegendSettings.yAxisValue}
                  updateSetting={updateSetting}
                />
              )}
            </>
          )}
          {numRunsWithCustomColors > 0 && (
            <div className="ml-14">
              {numRunsWithCustomColorsText}{' '}
              <span
                className="font-semibold text-teal-600 hover:text-teal-550"
                role="button"
                onClick={clearCustomColors}>
                Clear
                {numRunsWithCustomColors > 1 && ' all'}
              </span>
            </div>
          )}
        </div>
      )}
      {semanticLegendRunColorOptions ===
        RunColorOptions.OriginalProjectColors && <ColorPalette />}
      {showMetricBasedColors &&
        semanticLegendSettings.key !== DEFAULT_SEMANTIC_LEGEND_SETTINGS.key && (
          <>
            <div className="flex h-full w-full flex-col">
              <SemanticLegendNumBucketsWithCategoricalConfig
                numberOfBuckets={semanticLegendSettings.numberOfBuckets ?? 0}
                updateSetting={updateSetting}
              />

              {semanticLegendSettings?.yAxisValue ===
              YAxisValues.Config_Categories ? (
                <SemanticLegendCategories
                  allCategories={semanticLegendSettings.allCategories ?? []}
                  selectedCategories={
                    semanticLegendSettings.selectedCategories ?? []
                  }
                  categoryCustomizations={
                    semanticLegendSettings.bucketCustomizations
                  }
                  semanticLegendPalette={getSemanticLegendPalette(
                    semanticLegendSettings.yAxisValue
                  )}
                  updateSetting={updateSetting}
                  isLoadingBuckets={isLoadingBuckets}
                />
              ) : (
                <SemanticLegendBucketsWithCategoricalConfig
                  bucketCustomizations={
                    semanticLegendSettings?.bucketCustomizations ?? {}
                  }
                  buckets={semanticLegendSettings.buckets ?? []}
                  updateSetting={updateSetting}
                  isLoadingBuckets={isLoadingBuckets}
                />
              )}
              <SemanticLegendExcludedRunColor
                excludedRunColor={semanticLegendSettings.excludedRunColor ?? ''}
                updateSetting={updateSetting}
              />
            </div>
          </>
        )}
    </div>
  );
};
